import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { ChatBubbleLeftRightIcon, CheckIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { useEffect, useState } from 'react';
import { useChat } from '../../../../libs/hooks';
import { useAppDispatch, useAppSelector } from '../../../../redux/app/hooks';
import { RootState } from '../../../../redux/app/store';
import { setConversationDisplayMode } from '../../../../redux/features/app/appSlice';
import { IConversationDisplayMode } from '../../../../redux/features/app/AppState';
import { Conversations } from '../../../../redux/features/conversations/ConversationsState';
import { isToday } from '../../../utils/TextUtils';
import { LoadingMessage } from '../ChatWindow/Shared/LoadingMessage';
import { ChatListSection } from './ChatListSection';
import { NewChatMenu } from './NewChatMenu';

interface IChatListProps {
    toggleChatList: () => void;
}

interface ConversationsView {
    filteredConversations?: Conversations;
    latestConversations?: Conversations;
    olderConversations?: Conversations;
}

export enum AppState {
    ReloadingConversations,
    Chat,
}

export const ChatList: React.FC<IChatListProps> = ({ toggleChatList }) => {
    const chat = useChat();

    const dispatch = useAppDispatch();
    const [appState, setAppState] = useState(AppState.Chat);
    //const { activeUserInfo, features } = useAppSelector((state: RootState) => state.app);
    const { conversations, isLoadingMessages, isLoadingChats } = useAppSelector((state: RootState) => {
        return state.conversations;
    });
    const { conversationDisplayMode } = useAppSelector((state: RootState) => state.app);

    const [isFiltering] = useState(false);
    const [filterText] = useState('');
    const [conversationsView, setConversationsView] = useState<ConversationsView>({
        latestConversations: conversations,
    });
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [scrollToFetch, setScrollToFetch] = useState(false);
    const [selectedConversationOption, setSelectedConversationOption] = useState(
        `${IConversationDisplayMode[conversationDisplayMode]} conversations`,
    );

    const onAddChat = () => {
        dispatch(setConversationDisplayMode(IConversationDisplayMode.Active));
        void chat.createChat();
    };

    const onSelectChatOption = (selectedChatOption: string) => {
        const _conversationOption = selectedChatOption;

        setPage(1);
        setHasMore(true);
        setSelectedConversationOption(_conversationOption);
        setAppState(AppState.ReloadingConversations);

        switch (_conversationOption) {
            case 'Active conversations':
                dispatch(setConversationDisplayMode(IConversationDisplayMode.Active));
                break;
            case 'Hidden conversations':
                dispatch(setConversationDisplayMode(IConversationDisplayMode.Hidden));
                break;
            case 'All conversations':
                dispatch(setConversationDisplayMode(IConversationDisplayMode.All));
                break;
        }

        /* TODO: Pass empty [] for conversations or check if all conversation are already loaded */
        if (_conversationOption === 'Active conversations' || _conversationOption === 'All conversations') {
            void Promise.all([
                chat.loadChats(conversations, _conversationOption === 'All conversations').then((result) => {
                    if (result.success) {
                        setAppState(AppState.Chat);
                    }
                }),
            ]);
        } else {
            void Promise.all([
                chat
                    .loadHiddenChats(conversations)
                    .then((result) => {
                        if (result.success) {
                            setAppState(AppState.Chat);
                        } else {
                            console.error('Failed to load hidden chats');
                            setAppState(AppState.Chat);
                        }
                    })
                    .catch((error) => {
                        console.error('Failed to load hidden chats', error);
                        setAppState(AppState.Chat);
                    }),
            ]);
        }
    };

    const sortConversations = (conversations: Conversations): ConversationsView => {
        const sortedIds = Object.keys(conversations).sort((a, b) => {
            const aTimestamp = conversations[a].lastUpdatedTimestamp ?? 0;
            const bTimestamp = conversations[b].lastUpdatedTimestamp ?? 0;
            return new Date(bTimestamp).getTime() - new Date(aTimestamp).getTime();
        });

        const latestConversations: Conversations = {};
        const olderConversations: Conversations = {};

        sortedIds.forEach((id) => {
            //if (!conversations[id].isSaved) return;
            const timestamp = conversations[id].lastUpdatedTimestamp ?? 0;
            if (isToday(new Date(timestamp))) {
                latestConversations[id] = conversations[id];
            } else {
                olderConversations[id] = conversations[id];
            }
        });
        return {
            latestConversations,
            olderConversations,
        };
    };

    useEffect(() => {
        const nonHiddenConversations: Conversations = {};
        for (const key in conversations) {
            const conversation = conversations[key];
            if (!filterText || conversation.title.toLocaleUpperCase().includes(filterText.toLocaleUpperCase())) {
                nonHiddenConversations[key] = conversation;
            }
        }

        const allExistingConversations = {
            ...(conversationsView.latestConversations ?? {}),
            ...(conversationsView.olderConversations ?? {}),
            ...conversations,
        };

        const filteredChatList = Object.fromEntries(
            Object.entries(allExistingConversations).filter(([key]) => key in nonHiddenConversations),
        );

        setConversationsView(sortConversations(filteredChatList));
        setScrollToFetch(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [conversations, filterText]);

    const getNextConversations = async (page: number) => {
        let result;

        if (
            conversationDisplayMode === IConversationDisplayMode.Active ||
            conversationDisplayMode === IConversationDisplayMode.All
        ) {
            result = await chat.loadChats(
                conversations,
                selectedConversationOption == 'All conversations',
                page,
                undefined,
                true,
            );
        } else {
            result = await chat.loadHiddenChats(conversations, page, undefined, true);
        }

        if (result.success) {
            setAppState(AppState.Chat);
        }

        if (result.count === 0 || result.count < 20) {
            setHasMore(false);
            setScrollToFetch(false);
        }
    };

    useEffect(() => {
        if (page > 1) {
            getNextConversations(page);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page]);

    useEffect(() => {
        if (scrollToFetch) {
            setPage((prevPage) => prevPage + 1);
        }
    }, [scrollToFetch]);

    const fetchData = () => {
        if (hasMore) {
            setScrollToFetch(true);
        }
    };

    /*const onFilterClick = () => {
        setIsFiltering(true);
    };

    const onFilterCancel = () => {
        setFilterText('');
        setIsFiltering(false);
    };

    const onSearch = (ev: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => {
        ev.preventDefault();
        setFilterText(data.value);
    };*/

    return (
        <>
            <div className="absolute w-full lg:relative flex h-[100svh] flex-col overflow-y-auto bg-panel pt-2 md:pt-3 dark:border-slate-700 dark:bg-slate-700 sm:w-full sm:h-[100vh] md:w-80 z-20">
                <div className="flex items-center w-[calc(100%-18px)] mx-2">
                    <button
                        className="md:hidden inline-flex align-middle mr-1 md:w-full gap-x-2 cursor-pointer justify-center p-1 text-slate-700 hover:bg-slate-200 dark:text-slate-50 transition-all duration-200"
                        title="Close settings panel"
                        onClick={toggleChatList}>
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-6 w-6 rotate-180 align-middle text-primary-300"
                            strokeWidth="2"
                            stroke="currentColor"
                            fill="none"
                            strokeLinecap="round"
                            strokeLinejoin="round">
                            <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                            <path d="M4 4m0 2a2 2 0 0 1 2 -2h12a2 2 0 0 1 2 2v12a2 2 0 0 1 -2 2h-12a2 2 0 0 1 -2 -2z"></path>
                            <path d="M9 4v16"></path>
                            <path d="M14 10l2 2l-2 2"></path>
                        </svg>
                        <span className="sr-only">Close chat list sidebar</span>
                    </button>
                    <NewChatMenu newChat={onAddChat} />
                </div>
                <div className="mt-3 mx-2">
                    <Menu
                        as="div"
                        className="relative inline-block text-left w-[calc(100%-5px)] ml-1 md:ml-0 md:w-full rounded-lg border border-slate-300 dark:border-slate-600">
                        <div>
                            <MenuButton
                                disabled={appState === AppState.ReloadingConversations || isLoadingMessages}
                                className="inline-flex w-full justify-center gap-x-1.5 rounded-md px-3 py-2 font-medium shadow-sm hover:bg-slate-200 bg-transparent border-none  dark:hover:bg-slate-800 bg-white dark:bg-slate-800/50">
                                <ChatBubbleLeftRightIcon className="h-5 w-5" />
                                {selectedConversationOption}
                                <ChevronDownIcon aria-hidden="true" className="-mr-1 size-5" />
                            </MenuButton>
                        </div>
                        <MenuItems
                            transition
                            className="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black/5 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in overflow-hidden dark:bg-slate-800 text-slate-700 dark:text-slate-200 text-sm">
                            <div className="">
                                {['Active conversations', 'Hidden conversations', 'All conversations'].map((scope) => (
                                    <MenuItem key={scope}>
                                        <button
                                            onClick={() => onSelectChatOption(scope)}
                                            className="items-center flex flex-row w-full text-left px-3 py-2 data-[focus]:bg-gray-100 data-[focus]:text-gray-900 data-[focus]:outline-none dark:data-[focus]:text-slate-200 dark:data-[focus]:bg-slate-500 cursor-pointer">
                                            {selectedConversationOption === scope ? (
                                                <CheckIcon className="h-4 w-4 inline-block mr-2" />
                                            ) : (
                                                <span className="h-4 w-4 inline-block mr-2" />
                                            )}
                                            <span>{scope}</span>
                                        </button>
                                    </MenuItem>
                                ))}
                            </div>
                        </MenuItems>
                    </Menu>
                </div>
                {/* Previous chats container */}
                <div
                    id="chat-list"
                    className="h-screen space-y-4 overflow-y-auto border-b border-slate-300 px-2 py-4 dark:border-slate-700">
                    {isLoadingChats ? (
                        <LoadingMessage message="Loading chats..." />
                    ) : (
                        appState !== AppState.ReloadingConversations && (
                            <>
                                {Object.keys(conversations).length > 0 ? (
                                    <>
                                        {isFiltering && filterText.length > 0 ? (
                                            <>
                                                {conversationsView.filteredConversations && (
                                                    <ChatListSection
                                                        onSelectChat={toggleChatList}
                                                        conversations={conversationsView.filteredConversations}
                                                        fetchData={fetchData}
                                                        hasMore={hasMore}
                                                    />
                                                )}
                                            </>
                                        ) : (
                                            <>
                                                {conversationsView.latestConversations && (
                                                    <ChatListSection
                                                        onSelectChat={toggleChatList}
                                                        header="Today"
                                                        conversations={conversationsView.latestConversations}
                                                        fetchData={fetchData}
                                                        hasMore={hasMore}
                                                    />
                                                )}
                                                {conversationsView.olderConversations && (
                                                    <ChatListSection
                                                        onSelectChat={toggleChatList}
                                                        header="Older"
                                                        conversations={conversationsView.olderConversations}
                                                        fetchData={fetchData}
                                                        hasMore={hasMore}
                                                    />
                                                )}
                                            </>
                                        )}
                                    </>
                                ) : (
                                    <p>No conversations</p>
                                )}
                            </>
                        )
                    )}
                </div>
            </div>
        </>
    );
};
